import * as React from 'react';

import get from 'lodash/get';

import {Routing} from 'nutshell-core/routing';
import type {FilterObject} from 'nutshell-core/types';

import {FilterCapsule} from 'shells/filter-capsule/filter-capsule';

import styles from './metrics-form.module.css';

const DEFAULT_PERIOD = 'mtd';
const metricsFilters = [
    {
        name: 'dashboardPeriod',
        key: 'period',
        defaultValue: DEFAULT_PERIOD,
        defaultValueTitle: 'Date range: month-to-date',
    },
    {
        name: 'singleAssignee',
        key: 'filter.owner',
        defaultValue: undefined,
        defaultValueTitle: undefined,
    },
    {
        name: 'tags',
        key: 'filter.tags',
        defaultValue: undefined,
        defaultValueTitle: undefined,
    },
    {
        name: 'sources',
        key: 'filter.sources',
        defaultValue: undefined,
        defaultValueTitle: undefined,
    },
    {
        name: 'products',
        key: 'filter.products',
        defaultValue: undefined,
        defaultValueTitle: undefined,
    },
    {
        name: 'territory',
        key: 'filter.territories',
        defaultValue: undefined,
        defaultValueTitle: undefined,
    },
    {
        name: 'stageset',
        key: 'filter.stageset',
        defaultValue: undefined,
        defaultValueTitle: undefined,
    },
];

type Props = {
    value: string;
    schema: any;
    onFilterSubmit: (data: FilterObject, key: string) => void;
    onFilterRemove: (filterKey: string) => void;
};

export class MetricsForm extends React.Component<Props> {
    render() {
        const formValues = Routing.deparam(this.props.value);
        // Conditionally hide the pipeline filter if the instance
        // only has 1 active pipeline (no reason to add complexity!)
        const shownFilters = metricsFilters.filter((schemaFilter) => {
            if (NutClientConfig.numActivePipelines === 1 && schemaFilter.name === 'stageset') {
                // Before removing our pipeline filter entirely,
                // make sure we aren't actively filtering on it,
                // in the case that someone has archived their
                // second pipeline but had a filter applied.
                return Boolean(get(formValues, 'filter.stageset'));
            }

            return true;
        });

        return (
            <div className={styles.container}>
                <div className={styles.label}>Filters</div>
                <div className={styles['selectable-filters']}>
                    {shownFilters.map((filter) => {
                        // If the period is the default period, don't show the removal "x"
                        const currentFilter =
                            filter.key === 'period' &&
                            get(formValues, filter.key) === DEFAULT_PERIOD
                                ? undefined
                                : get(formValues, filter.key);

                        return (
                            <FilterCapsule
                                key={filter.key}
                                filter={currentFilter}
                                filterName={filter.name}
                                schemaField={this.props.schema[filter.name]}
                                // @ts-expect-error
                                defaultValue={filter.defaultValue}
                                defaultValueTitle={filter.defaultValueTitle}
                                onSubmit={(data) => this.props.onFilterSubmit(data, filter.key)}
                                // @ts-expect-error
                                onUpdate={(data) => this.props.onFilterSubmit(data, filter.key)}
                                onRemove={() => this.handleFilterRemove(filter.key)}
                                className={styles.filter}
                            />
                        );
                    })}
                </div>
            </div>
        );
    }

    /**
     * When the "x" on a filter is clicked, what should happen?
     * If the filter has a default value, we want to reset to that.
     * Otherwise, we remove the filter altogether
     *
     * This is necessary because if we clear out the period completely, the
     * dash itself won't know what period to display.
     *
     * @param  {string} key - Filter key
     * @return {void}
     */
    handleFilterRemove = (key: string) => {
        const filter = metricsFilters.filter((f) => f.key === key)[0];
        // @ts-expect-error
        if (filter.defaultValue) {
            // @ts-expect-error
            this.props.onFilterSubmit({dashboardPeriod: filter.defaultValue}, key);
        } else {
            this.props.onFilterRemove(key);
        }
    };
}
